import { FC, useEffect } from "react";
import Confetti from "react-confetti";
import classNames from "classnames";
import Modal from "../Modal";

export type CouponRedeemedValue = 50 | 100 | null;

interface CouponRedeemedAnimationProps {
  /**
   * Coupon percentage to celebrate. `null` hides the animation.
   * When the value changes from `null` to `50`/`100`, the audio + confetti play.
   */
  couponValue: CouponRedeemedValue;
  /** Called when the user dismisses the modal. */
  onClose: () => void;
}

const AUDIO_SRC = "/audio/TEALCA Sonido Promoción.mp3";

export const CouponRedeemedAnimation: FC<CouponRedeemedAnimationProps> = ({
  couponValue,
  onClose,
}) => {
  useEffect(() => {
    if (couponValue === null) return;

    let audio: HTMLAudioElement | null = null;
    try {
      audio = new Audio(AUDIO_SRC);
      audio.volume = 0;
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => {
          // Autoplay can be blocked by the browser; the GIF + confetti still play.
        });
      }
    } catch {
      return;
    }

    let volume = 0;
    let peak = false;
    const audioRef = audio;
    const interval = setInterval(() => {
      if (!audioRef) {
        clearInterval(interval);
        return;
      }
      if (!peak && volume < 1) {
        volume += 0.025;
        audioRef.volume = Math.min(1, volume);
      } else if (!peak) {
        peak = true;
      } else if (peak && volume > 0) {
        volume -= 0.025;
        audioRef.volume = Math.max(0, volume);
      } else {
        clearInterval(interval);
      }
    }, 100);

    return () => {
      clearInterval(interval);
      if (audioRef) {
        audioRef.pause();
        audioRef.currentTime = 0;
      }
    };
  }, [couponValue]);

  const handleModalClose: React.Dispatch<React.SetStateAction<boolean>> = (
    next
  ) => {
    // Headless UI passes the new `open` value (boolean). Only act on closes.
    if (typeof next === "function") return;
    if (!next) onClose();
  };

  const open = couponValue !== null;

  return (
    <>
      <Modal
        openModal={couponValue === 100}
        setOpenModal={handleModalClose}
        className="!p-0 overflow-hidden"
      >
        <img src="/video/Coupon100.gif" alt="Promoción 100% descuento" />
      </Modal>
      <Modal
        openModal={couponValue === 50}
        setOpenModal={handleModalClose}
        className="!p-0 overflow-hidden"
      >
        <img src="/video/Coupon50.gif" alt="Promoción 50% descuento" />
      </Modal>
      <Confetti className={classNames(!open && "hidden")} />
    </>
  );
};

export default CouponRedeemedAnimation;
